import React, { useState, useEffect } from 'react'
import InputFields from '../components/InputFields';
import { useNavigate } from "react-router-dom"
import { showErrorToast, showSuccessToast } from '../components/common';
import { createCaterer } from '../API/caterers';

import './../i18n'; // Import the i18n configuration
import { useTranslation } from 'react-i18next';
import { useAddCaterer, useGetCatererById, useUpdateCaterer, useCreateSubscription, useUpdateUserOnboardingStatus } from '../hooks/hooks';


const Onboarding = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { mutate: addCaterer } = useAddCaterer();
    const { mutate: updateCaterer } = useUpdateCaterer();
    const { mutate: createSubscription } = useCreateSubscription();
    const { mutate: updateUserOnboardingStatus } = useUpdateUserOnboardingStatus();
   
    const [currentStep, setCurrentStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const currentUser = JSON.parse(localStorage.getItem('CurrentUser') || '{}');
    
    // Helper function to get current user ID from localStorage
    const getCurrentUserId = () => {
        try {
            const currentUser = JSON.parse(localStorage.getItem("CurrentUser") || "{}");
            return currentUser?._id || currentUser?.id;
        } catch (error) {
            console.error('Error parsing CurrentUser from localStorage:', error);
            return null;
        }   
    };

    // Function to handle logout and navigate to login
    const handleBackToLogin = () => {
        setShowLogoutConfirm(true);
    };

    const confirmLogout = () => {
        // Clear user data from localStorage
        localStorage.removeItem('CurrentUser');
        localStorage.removeItem('AccessToken');
        // Navigate to login page
        navigate('/login');
    };

    const cancelLogout = () => {
        setShowLogoutConfirm(false);
    };

    const { data: catererData, isLoading: catererLoading, error: catererError, refetch: refetchCaterer } = useGetCatererById(getCurrentUserId());
    
    // Check if user is authenticated and onboarding status
    useEffect(() => {
        try {
            const accessToken = localStorage.getItem('AccessToken');
            
            if (!accessToken || !currentUser) {
                navigate('/login');
                return;
            }
            
            // Check if onboarding is already completed
            if (currentUser?.isOnboarding === true) {
                navigate('/dashboard');
                return;
            }
        } catch (error) {
            console.error('Error checking authentication and onboarding status:', error);
            navigate('/login');
        }
    }, [navigate]);

    // Populate form with existing caterer data if it exists
    useEffect(() => {
        if (catererData &&  catererData.caterer &&   !catererLoading) {
            setCatererDetails({
                name: catererData.caterer.name || '',
                address: catererData.caterer.address || '',
                phoneNumber: catererData.caterer.phone || '',
                state: catererData.caterer.state || '',
                city: catererData.caterer.city || '',
                postalCode: catererData.caterer.postalCode || '',
                businessRegistrationNumber: catererData.caterer.businessRegistrationNumber || '',
                website: catererData.caterer.website || '',
                email : currentUser?.email || ''
            });
        }
    }, [catererData, catererLoading, currentUser?.email]);
    
    // Step 1: Caterer Details
    const [catererDetails, setCatererDetails] = useState({
        name: '',
        address: '',
        email: currentUser?.email || '',
        phoneNumber: '',
        state: '',
        city: '',
        postalCode: '',
        businessRegistrationNumber: '',
        website: ''
    });
    const [catererErrors, setCatererErrors] = useState({
        name: '',
        address: '',
        email: '',
        phoneNumber: '',
        state: '',
        city: '',
        postalCode: '',
        businessRegistrationNumber: '',
        website: ''
    });

    // Indian states and cities data
    const indianStates = [
        'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
        'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
        'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
        'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
        'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
        'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
        'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
    ];

    const indianCities = {
        'Andhra Pradesh': ['Hyderabad', 'Visakhapatnam', 'Vijayawada', 'Guntur', 'Nellore'],
        'Arunachal Pradesh': ['Itanagar', 'Naharlagun', 'Pasighat', 'Tezpur', 'Namsai'],
        'Assam': ['Guwahati', 'Silchar', 'Dibrugarh', 'Jorhat', 'Nagaon'],
        'Bihar': ['Patna', 'Gaya', 'Bhagalpur', 'Muzaffarpur', 'Darbhanga'],
        'Chhattisgarh': ['Raipur', 'Bhilai', 'Bilaspur', 'Korba', 'Rajnandgaon'],
        'Goa': ['Panaji', 'Margao', 'Vasco da Gama', 'Mapusa', 'Ponda'],
        'Gujarat': ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Bhavnagar'],
        'Haryana': ['Chandigarh', 'Faridabad', 'Gurgaon', 'Panipat', 'Hisar'],
        'Himachal Pradesh': ['Shimla', 'Mandi', 'Solan', 'Dharamshala', 'Baddi'],
        'Jharkhand': ['Ranchi', 'Jamshedpur', 'Dhanbad', 'Bokaro', 'Deoghar'],
        'Karnataka': ['Bangalore', 'Mysore', 'Hubli', 'Mangalore', 'Belgaum'],
        'Kerala': ['Thiruvananthapuram', 'Kochi', 'Kozhikode', 'Thrissur', 'Kollam'],
        'Madhya Pradesh': ['Bhopal', 'Indore', 'Gwalior', 'Jabalpur', 'Ujjain'],
        'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Nashik', 'Aurangabad'],
        'Manipur': ['Imphal', 'Thoubal', 'Bishnupur', 'Churachandpur', 'Senapati'],
        'Meghalaya': ['Shillong', 'Tura', 'Jowai', 'Nongstoin', 'Williamnagar'],
        'Mizoram': ['Aizawl', 'Lunglei', 'Saiha', 'Champhai', 'Kolasib'],
        'Nagaland': ['Kohima', 'Dimapur', 'Mokokchung', 'Tuensang', 'Wokha'],
        'Odisha': ['Bhubaneswar', 'Cuttack', 'Rourkela', 'Berhampur', 'Sambalpur'],
        'Punjab': ['Chandigarh', 'Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala'],
        'Rajasthan': ['Jaipur', 'Jodhpur', 'Udaipur', 'Kota', 'Bikaner'],
        'Sikkim': ['Gangtok', 'Namchi', 'Mangan', 'Gyalshing', 'Ravangla'],
        'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Tiruchirappalli', 'Salem'],
        'Telangana': ['Hyderabad', 'Warangal', 'Nizamabad', 'Khammam', 'Karimnagar'],
        'Tripura': ['Agartala', 'Dharmanagar', 'Udaipur', 'Ambassa', 'Kailasahar'],
        'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Agra', 'Varanasi', 'Meerut'],
        'Uttarakhand': ['Dehradun', 'Haridwar', 'Rishikesh', 'Haldwani', 'Roorkee'],
        'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Asansol', 'Siliguri'],
        'Andaman and Nicobar Islands': ['Port Blair', 'Diglipur', 'Mayabunder', 'Rangat', 'Havelock'],
        'Chandigarh': ['Chandigarh'],
        'Dadra and Nagar Haveli and Daman and Diu': ['Daman', 'Diu', 'Silvassa'],
        'Delhi': ['New Delhi', 'Central Delhi', 'East Delhi', 'North Delhi', 'South Delhi'],
        'Jammu and Kashmir': ['Srinagar', 'Jammu', 'Anantnag', 'Baramulla', 'Kathua'],
        'Ladakh': ['Leh', 'Kargil'],
        'Lakshadweep': ['Kavaratti', 'Agatti', 'Amini', 'Andrott', 'Kadmat'],
        'Puducherry': ['Puducherry', 'Karaikal', 'Mahe', 'Yanam']
    };

    const handleCatererChange = (e) => {
        const { name, value } = e.target;
        setCatererDetails({
            ...catererDetails,
            [name]: value.toString(),
        });
        setCatererErrors({ ...catererErrors, [name]: '' });
    };

    const validateStep1 = () => {
        let isValid = true;
        const errors = {};

        if (catererDetails.name.toString().trim() === "") {
            errors.name = 'Please enter business name';
            isValid = false;
        }

        if (catererDetails.address.toString().trim() === "") {
            errors.address = 'Please enter address';
            isValid = false;
        }

        if (catererDetails.phoneNumber.toString().trim() === "") {
            errors.phoneNumber = 'Please enter phone number';
            isValid = false;
        }

        const phoneRegex = /^(\+91|91)?[6-9]\d{9}$/;
        const cleanPhone = catererDetails.phoneNumber.replace(/\s+/g, '').replace(/[^\d+]/g, '');
        
        if (!phoneRegex.test(cleanPhone)) {
            errors.phoneNumber = 'Please enter a valid phone number (10 digits starting with 6-9)'
            isValid = false;
        }

        if (catererDetails.state.toString().trim() === "") {
            errors.state = 'Please select state';
            isValid = false;
        }

        if (catererDetails.city.toString().trim() === "") {
            errors.city = 'Please select city';
            isValid = false;
        }

        if (catererDetails.postalCode.toString().trim() === "") {
            errors.postalCode = 'Please enter postal code';
            isValid = false;
        }

        const postalCodeRegex = /^[1-9][0-9]{5}$/;
        if (catererDetails.postalCode.toString().trim() !== "" && !postalCodeRegex.test(catererDetails.postalCode)) {
            errors.postalCode = 'Please enter a valid 6-digit postal code';
            isValid = false;
        }

        if (catererDetails.businessRegistrationNumber.toString().trim() === "") {
            errors.businessRegistrationNumber = 'Please enter business registration number';
            isValid = false;
        }

        setCatererErrors(errors);
        return isValid;
    };

    const handleNextStep = async () => {
        if (currentStep === 1) {
            if (validateStep1()) {
                setIsLoading(true);
                
                // Get user ID from localStorage
                const ownerId = getCurrentUserId();
                
                if (!ownerId) {
                    showErrorToast("User information not found. Please login again.");
                    navigate('/login');
                    setIsLoading(false);
                    return;
                }

                // Prepare the payload for the API
                const payload = {
                    ownerId: ownerId,
                    name: catererDetails.name,
                    address: catererDetails.address,
                    email : catererDetails.email || null,
                    phone: catererDetails.phoneNumber,
                    state: catererDetails.state,
                    city: catererDetails.city,
                    postalCode: catererDetails.postalCode,
                    businessRegistrationNumber: catererDetails.businessRegistrationNumber,
                    website: catererDetails.website || null
                };

                // Check if caterer already exists to decide between create or update
                if (catererData && catererData?.caterer && catererData?.caterer._id) {
                    // Update existing caterer using mutation
                    updateCaterer(
                        { id: catererData?.caterer?._id, data: payload },
                        {
                            onSuccess: (response) => {
                                if (response?.data?.success === true) {
                                    // showSuccessToast("Caterer details updated successfully!");
                                    setCurrentStep(2);
                                } else {
                                    const errorMessage = response?.data?.message || "Failed to update caterer details. Please try again.";
                                    showErrorToast(errorMessage);
                                }
                                setIsLoading(false);
                            },
                            onError: (error) => {
                                console.error('Error updating caterer:', error);
                                const errorMessage = error.response?.data?.message || "Failed to update caterer details. Please try again.";
                                showErrorToast(errorMessage);
                                setIsLoading(false);
                            }
                        }
                    );
                } else {
                    // Create new caterer using mutation
                    addCaterer(payload, {
                        onSuccess: (response) => {
                            if (response?.data?.success === true) {
                                refetchCaterer();
                                // showSuccessToast("Caterer details saved successfully!");
                                setCurrentStep(2);
                            } else {
                                const errorMessage = response?.data?.message || "Failed to save caterer details. Please try again.";
                                showErrorToast(errorMessage);
                            }
                            setIsLoading(false);
                        },
                        onError: (error) => {
                            console.error('Error creating caterer:', error);
                            const errorMessage = error.response?.data?.message || "Failed to save caterer details. Please try again.";
                            showErrorToast(errorMessage);
                            setIsLoading(false);
                        }
                    });
                }
            }
        }
    };

    const handlePreviousStep = () => {
        if (currentStep === 2) {
            setCurrentStep(1);
        }
    };

    const handlePlanSelection = (plan) => {
        setSelectedPlan(plan);
        setStep2Data({ ...step2Data, selectedPlan: plan });
    };

    const handleSubmit = () => {
        if (!selectedPlan) {
            showErrorToast("Please select a subscription plan to continue");
            return;
        }
        
        setIsLoading(true);
        
        // Get current user ID and caterer ID
        const ownerId = getCurrentUserId();
        const catererId = catererData?.caterer?._id;
        
        if (!ownerId || !catererId) {
            showErrorToast("User or caterer information not found. Please try again.");
            setIsLoading(false);
            return;
        }
        
        // Calculate subscription dates (start from today, end after 1 month)
        const startDate = new Date().toISOString().split('T')[0]; // Today's date in YYYY-MM-DD format
        const endDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // 30 days from today
        
        // Get plan details based on selection
        const getPlanDetails = (plan) => {
            switch (plan) {
                case 'basic':
                    return { price: 999, planName: 'Basic Plan' };
                case 'professional':
                    return { price: 1999, planName: 'Professional Plan' };
                case 'enterprise':
                    return { price: 3999, planName: 'Enterprise Plan' };
                default:
                    return { price: 999, planName: 'Basic Plan' };
            }
        };
        
        const { price, planName } = getPlanDetails(selectedPlan);
        
        // Prepare subscription payload
        const subscriptionPayload = {
            ownerId,
            catererId,
            planName,
            startDate,
            endDate,
            price,
            paymentId: `PAY_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` // Generate temporary payment ID
        };
        
        // Create subscription using mutation
        createSubscription(subscriptionPayload, {
            onSuccess: (response) => {
                if (response?.data?.success === true) {
                    // After successful subscription, update user onboarding status
                    const userId = getCurrentUserId();
                    updateUserOnboardingStatus(
                        { 
                            userId, 
                            onboardingData: { isOnboarding: true } 
                        },
                        {
                            onSuccess: (userResponse) => {
                                if (userResponse?.data?.success === true) {
                                    // Update localStorage to reflect onboarding completion
                                    try {
                                        const currentUser = JSON.parse(localStorage.getItem('CurrentUser') || '{}');
                                        const updatedUser = { 
                                            ...currentUser, 
                                            isOnboarding: true, 
                                            catererDetails : catererData?.caterer
                                        };
                                        localStorage.setItem('CurrentUser', JSON.stringify(updatedUser));
                                        
                                        // Dispatch custom event to notify other components
                                        window.dispatchEvent(new CustomEvent('onboardingComplete'));
                                    } catch (error) {
                                        console.error('Error updating localStorage:', error);
                                    }
                                    
                                    showSuccessToast(`Subscription created successfully! Welcome to ${planName}`);
                                    setIsLoading(false);
                                    navigate('/dashboard');
                                } else {
                                    const errorMessage = userResponse?.data?.message || "Failed to update onboarding status. Please try again.";
                                    showErrorToast(errorMessage);
                                    setIsLoading(false);
                                }
                            },
                            onError: (userError) => {
                                console.error('Error updating user onboarding status:', userError);
                                const errorMessage = userError.response?.data?.message || "Failed to update onboarding status. Please try again.";
                                showErrorToast(errorMessage);
                                setIsLoading(false);
                            }
                        }
                    );
                } else {
                    const errorMessage = response?.data?.message || "Failed to create subscription. Please try again.";
                    showErrorToast(errorMessage);
                    setIsLoading(false);
                }
            },
            onError: (error) => {
                console.error('Error creating subscription:', error);
                const errorMessage = error.response?.data?.message || "Failed to create subscription. Please try again.";
                showErrorToast(errorMessage);
                setIsLoading(false);
            }
        });
    };

    const renderStepIndicator = () => (
        <div className="flex items-center justify-center mb-8">
            <div className="flex items-center space-x-4">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                    currentStep >= 1 
                        ? 'bg-primary border-primary text-white' 
                        : 'bg-white/10 border-white/20 text-gray-400'
                }`}>
                    <span className="text-sm font-semibold">1</span>
                </div>
                <div className={`w-16 h-1 rounded ${
                    currentStep >= 2 ? 'bg-primary' : 'bg-white/20'
                }`}></div>
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                    currentStep >= 2 
                        ? 'bg-primary border-primary text-white' 
                        : 'bg-white/10 border-white/20 text-gray-400'
                }`}>
                    <span className="text-sm font-semibold">2</span>
                </div>
            </div>
        </div>
    );

    const renderStep1 = () => (
        <div className="space-y-6">
            <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-2 animate-fade-in">Caterers Details</h2>
            <p className="text-gray-300 animate-fade-in animation-delay-300">Tell us about your business</p>
            </div>

            {/* Form Fields */}
            <div className="space-y-6 animate-fade-in-up animation-delay-300">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Caterer Name */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-white/90">
                            Caterer Name *
                        </label>
                        <InputFields
                            type="text"
                            name="name"
                            value={catererDetails.name}
                            handleChange={handleCatererChange}
                            placeholder="Enter caterer name"
                            error={catererErrors.name}
                            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                        />
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-white/90">
                            Email *
                        </label>
                        <InputFields
                            type="email"
                            name="email"
                            value={catererDetails.email}
                            handleChange={handleCatererChange}
                            placeholder="Enter email address"
                            error={catererErrors.email}
                            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                        />
                    </div>
                </div>

                {/* Address - Full Width */}
                <div className="space-y-2">
                                            <label className="block text-sm font-medium text-white/90">
                            Address *
                        </label>
                    <InputFields
                        type="text"
                        name="address"
                        value={catererDetails.address}
                        handleChange={handleCatererChange}
                        placeholder="Enter complete address"
                        error={catererErrors.address}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* State */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-white/90">
                            State *
                        </label>
                        <div className="relative">
                            <select
                                name="state"
                                value={catererDetails.state}
                                onChange={handleCatererChange}
                                className="appearance-none w-full h-12 px-4 pr-10 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                            >
                                <option value="" className="bg-gray-800 text-white">Select State</option>
                                {indianStates.map(state => (
                                    <option key={state} value={state} className="bg-gray-800 text-white">{state}</option>
                                ))}
                            </select>
                            <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                        {catererErrors.state && <p className="text-red-400 text-sm mt-1">{catererErrors.state}</p>}
                    </div>

                    {/* City */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-white/90">
                            City *
                        </label>
                        <div className="relative">
                            <select
                                name="city"
                                value={catererDetails.city}
                                onChange={handleCatererChange}
                                disabled={!catererDetails.state}
                                className="appearance-none w-full h-12 px-4 pr-10 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <option value="" className="bg-gray-800 text-white">Select City</option>
                                {catererDetails.state && indianCities[catererDetails.state]?.map(city => (
                                    <option key={city} value={city} className="bg-gray-800 text-white">{city}</option>
                                ))}
                            </select>
                            <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </div>
                        {catererErrors.city && <p className="text-red-400 text-sm mt-1">{catererErrors.city}</p>}
                    </div>

                    {/* Postal Code */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-white/90">
                            Postal Code *
                        </label>
                        <InputFields
                            type="text"
                            name="postalCode"
                            value={catererDetails.postalCode}
                            handleChange={handleCatererChange}
                            placeholder="Enter postal code"
                            error={catererErrors.postalCode}
                            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Phone Number */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-white/90">
                            Phone Number *
                        </label>
                        <InputFields
                            type="tel"
                            name="phoneNumber"
                            value={catererDetails.phoneNumber}
                            handleChange={handleCatererChange}
                            placeholder="Enter phone number"
                            error={catererErrors.phoneNumber}
                            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                        />
                    </div>

                    {/* Business Registration Number */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-white/90">
                            Business Registration Number *
                        </label>
                        <InputFields
                            type="text"
                            name="businessRegistrationNumber"
                            value={catererDetails.businessRegistrationNumber}
                            handleChange={handleCatererChange}
                            placeholder="Enter registration number"
                            error={catererErrors.businessRegistrationNumber}
                            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                        />
                    </div>
                </div>

                {/* Website - Full Width */}
                <div className="space-y-2">
                    <label className="block text-sm font-medium text-white/90">
                        Website
                    </label>
                    <InputFields
                        type="url"
                        name="website"
                        value={catererDetails.website}
                        handleChange={handleCatererChange}
                        placeholder="Enter website URL"
                        error={catererErrors.website}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-300"
                    />
                </div>
            </div>

            {/* Next Button */}
            <div className="pt-6 animate-fade-in-up animation-delay-900 flex justify-center">
                <button
                    onClick={handleNextStep}
                    disabled={isLoading}
                    className="px-8 py-3 h-12 bg-gradient-to-r from-primary to-secondary text-white font-medium rounded-lg hover:from-secondary hover:to-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-transparent transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
                >
                    <span>{isLoading ? 'Saving...' : (catererData?.caterer && catererData?.caterer._id ? 'Update & Continue' : 'Next Step')}</span>
                    {!isLoading && (
                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                    )}
                </button>
            </div>
        </div>
    );

    // Step 2: Will be added later
    const [step2Data, setStep2Data] = useState({});
    const [step2Errors, setStep2Errors] = useState({});
    const [selectedPlan, setSelectedPlan] = useState('Basic Plan');

    const renderStep2 = () => (
        <div className="space-y-6">
            <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-white mb-2 animate-fade-in">Choose Your Plan</h2>
                <p className="text-gray-300 animate-fade-in animation-delay-300">Select the perfect subscription plan for your business</p>
            </div>

            {/* Pricing Plans */}
            <div className="grid grid-cols-1 gap-6 mb-8">
                {/* Basic Plan */}
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 hover:border-primary/50 transition-all duration-300 hover:scale-105">
                    <div className="text-center">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">Basic Plan</h3>
                        <div className="text-3xl font-bold text-primary mb-1">₹999<span className="text-lg text-gray-300">/month</span></div>
                        <p className="text-gray-300 text-sm mb-6">Perfect for small caterers</p>
                    </div>
                    
                    <ul className="space-y-3 mb-6">
                        <li className="flex items-center text-gray-300">
                            <svg className="w-5 h-5 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                             Up to 50 menu items
                        </li>
                        <li className="flex items-center text-gray-300">
                            <svg className="w-5 h-5 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Basic inventory management
                        </li>
                        <li className="flex items-center text-gray-300">
                            <svg className="w-5 h-5 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Order management
                        </li>
                        <li className="flex items-center text-gray-300">
                            <svg className="w-5 h-5 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Email support
                        </li>
                    </ul>
                    
                    <button 
                        onClick={() => handlePlanSelection('basic')}
                        className={`w-full py-3 px-4 rounded-xl font-semibold transition-all duration-300 ${
                            selectedPlan === 'basic' 
                                ? 'bg-primary text-white border-2 border-primary' 
                                : 'bg-white/10 text-white border-2 border-white/20 hover:bg-white/20'
                        }`}
                    >
                        {selectedPlan === 'basic' ? 'Selected' : 'Choose Plan'}
                    </button>
                </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-6 space-x-3">
                <button
                    onClick={handlePreviousStep}
                    disabled={isLoading}
                    className="px-8 py-3 h-12 bg-white/10 text-white font-medium rounded-lg hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/20 focus:ring-offset-2 focus:ring-offset-transparent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.707 14.707a1 1 0 01-1.414 0L1.586 10l4.707-4.707a1 1 0 011.414 1.414L4.414 9H18a1 1 0 110 2H4.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                    Previous
                </button>
                <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-8 py-3 h-12 bg-gradient-to-r from-primary to-secondary text-white font-medium rounded-lg hover:from-secondary hover:to-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-transparent transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
                >
                    {isLoading ? (
                        <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Completing...
                        </>
                    ) : (
                        <>
                            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Complete Setup
                        </>
                    )}
                </button>
            </div>
        </div>
    );

    const renderUserInfo = () => (
        <div 
        // className="bg-gradient-to-r from-white/15 to-white/5 backdrop-blur-lg rounded-3xl p-6 border border-white/30 mb-8 shadow-2xl animate-fade-in-down"
        className="rounded-3xl border border-white/10 p-6  mb-8 shadow-2xl animate-fade-in-down">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                {/* User Profile Section */}
                <div className="flex items-center space-x-4">
                    <div className="relative">
                        <div className="w-16 h-16 bg-gradient-to-br from-primary via-secondary to-green-400 rounded-2xl flex items-center justify-center shadow-lg">
                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                        </div>
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-400 rounded-full border-2 border-white flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                        </div>
                    </div>
                    <div className="space-y-1">
                        <h3 className="text-white font-bold text-lg">
                            Welcome, {currentUser?.name || currentUser?.firstName || 'User'}! 👋
                        </h3>
                        <p className="text-gray-200 text-sm flex items-center">
                            <svg className="w-4 h-4 mr-2 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            {currentUser?.email || 'No email provided'}
                        </p>
                        <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                            <span className="text-green-300 text-xs font-medium">Account Verified</span>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                    <button
                        onClick={handleBackToLogin}
                        className="group flex items-center justify-center space-x-2 px-4 py-3 bg-white/10 hover:bg-red-500/20 text-white text-sm font-medium rounded-xl border border-white/20 hover:border-red-400/50 transition-all duration-300 hover:scale-105 hover:shadow-lg"
                    >
                        <svg className="w-4 h-4 group-hover:rotate-12 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        <span>Switch Account</span>
                    </button>
                </div>
            </div>

            {/* Progress Indicator */}
            <div className="mt-4 pt-4 border-t border-white/20">
                <div className="flex items-center justify-between text-xs text-gray-300 mb-2">
                    <span>Onboarding Progress</span>
                    <span>{currentStep}/2 Steps</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2">
                    <div 
                        className="bg-gradient-to-r from-primary to-secondary h-2 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${(currentStep / 2) * 100}%` }}
                    ></div>
                </div>
            </div>
        </div>
    );

    const renderLogoutModal = () => (
        showLogoutConfirm && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/30 shadow-2xl max-w-md w-full mx-4 animate-scale-in">
                    <div className="text-center">
                        <div className="w-16 h-16 bg-red-500/20 rounded-full mx-auto mb-4 flex items-center justify-center">
                            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">Switch Account?</h3>
                        <p className="text-gray-300 mb-6">
                            You'll need to log in again to access your account. Your current progress will be saved.
                        </p>
                        <div className="flex space-x-3">
                            <button
                                onClick={cancelLogout}
                                className="flex-1 px-4 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-all duration-300"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmLogout}
                                className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-medium rounded-xl transition-all duration-300 hover:scale-105"
                            >
                                Switch Account
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-primary to-secondary relative overflow-hidden">
            {/* Enhanced Animated Background Elements */}
            <div className="absolute inset-0">
                {/* Additional Smaller Orbs */}
                <div className="absolute top-1/4 left-1/3 w-32 h-32 bg-primary/50 rounded-full mix-blend-multiply filter blur-lg opacity-60 animate-pulse"></div>
                <div className="absolute bottom-1/4 right-1/3 w-24 h-24 bg-secondary/50 rounded-full mix-blend-multiply filter blur-lg opacity-60 animate-pulse animation-delay-1000"></div>
                <div className="absolute top-3/4 left-1/4 w-20 h-20 bg-green-400/50 rounded-full mix-blend-multiply filter blur-lg opacity-60 animate-pulse animation-delay-3000"></div>
                
                {/* Floating Particles */}
                <div className="absolute top-1/4 right-1/4 w-2 h-2 bg-white/30 rounded-full animate-bounce"></div>
                <div className="absolute top-1/3 left-1/5 w-1 h-1 bg-white/40 rounded-full animate-bounce animation-delay-500"></div>
                <div className="absolute bottom-1/3 right-1/5 w-1.5 h-1.5 bg-white/30 rounded-full animate-bounce animation-delay-1000"></div>
                <div className="absolute top-2/3 left-2/3 w-1 h-1 bg-white/50 rounded-full animate-bounce animation-delay-1500"></div>
                <div className="absolute top-1/2 right-1/3 w-1 h-1 bg-white/40 rounded-full animate-bounce animation-delay-2000"></div>
                
                {/* Wave Effects */}
                <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-primary/20 to-transparent animate-wave"></div>
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-secondary/10 to-transparent animate-wave animation-delay-2000"></div>
                
                {/* Rotating Rings */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 border border-white/10 rounded-full animate-spin-slow"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 border border-white/5 rounded-full animate-spin-slow animation-delay-1000" style={{ animationDirection: 'reverse' }}></div>
                
                {/* Grid Pattern with Animation */}
                <div className="absolute inset-0 opacity-20 animate-pulse" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    backgroundSize: '60px 60px'
                }}></div>
                
                {/* Glowing Spots */}
                <div className="absolute top-1/4 left-1/4 w-4 h-4 bg-primary/30 rounded-full animate-ping"></div>
                <div className="absolute bottom-1/4 right-1/4 w-3 h-3 bg-secondary/30 rounded-full animate-ping animation-delay-1000"></div>
                <div className="absolute top-3/4 right-1/3 w-2 h-2 bg-green-400/30 rounded-full animate-ping animation-delay-2000"></div>
            </div>

            {/* Main Content */}
            <div className="relative z-10 flex min-h-screen">
               
                <div className="w-full  flex items-center justify-center p-8">
                    <div className="w-full max-w-4xl">
                        {/* User Info Display */}
                        {renderUserInfo()}
                        
                        {/* Onboarding Card */}
                        <div className="bg-white/10 backdrop-blur-md rounded-3xl p-8 border border-white/20 shadow-2xl animate-fade-in-up">
                            {/* Step Indicator */}
                            {renderStepIndicator()}

                            {/* Step Content */}
                            {currentStep === 1 ? renderStep1() : renderStep2()}
                        </div>
                    </div>
                </div>
            </div>

            {/* Logout Confirmation Modal */}
            {renderLogoutModal()}
        </div>
    );
};

export default Onboarding;